/**
 * useListFilters — filter + pagination state for list pages, remembered so a
 * user who filters, opens a record and comes back does not have to redo it.
 *
 * Precedence on mount: URL query params > localStorage > defaults.
 *   - URL first so existing deep links keep working (e.g. the dashboard
 *     Incident Snapshot cards link to /incidents?status=Open&date_from=...).
 *   - localStorage second so a bare visit restores the last-used filters.
 *   - defaults last.
 *
 * Writes follow the house style set by dashboard/hooks/useTimeFilter.js:
 * setSearchParams(..., { replace: true }) so filtering never spams history.
 *
 * This hook owns state + persistence only — it deliberately does NOT filter,
 * so it suits both server-filtered lists (IncidentsList) and client-filtered
 * ones (HazardsList).
 *
 * Free-text search is never written to localStorage: it can contain a person's
 * name and must not outlive the session on a shared device. It still rides in
 * the URL, so refresh and back-navigation keep it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_VERSION = 1;
export const LIST_FILTER_STORAGE_PREFIX = 'saarv_list_filters:';

/** camelCase filter key -> snake_case query param (dateFrom -> date_from). */
const toParam = (key) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/**
 * Coerce one restored value. Anything a `sanitize` entry rejects, or any value
 * whose type no longer matches the default, falls back to the default rather
 * than reaching the UI as an unrepresentable state.
 */
function coerce(key, value, defaults, sanitize) {
  const fallback = defaults[key];
  if (value === undefined || value === null) return fallback;
  if (typeof sanitize[key] === 'function') return sanitize[key](value, fallback);
  if (typeof fallback === 'string' && typeof value !== 'string') return fallback;
  return value;
}

function readFromUrl(searchParams, defaults, sanitize) {
  const out = {};
  let found = false;
  for (const key of Object.keys(defaults)) {
    const raw = searchParams.get(toParam(key));
    if (raw === null) {
      out[key] = defaults[key];
    } else {
      found = true;
      out[key] = coerce(key, raw, defaults, sanitize);
    }
  }
  return found ? out : null;
}

function readFromStorage(storageKey, defaults, sanitize) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Version guard: a stale shape from an older build is discarded, not merged.
    if (!parsed || parsed.v !== STORAGE_VERSION || typeof parsed.filters !== 'object') return null;
    const out = {};
    for (const key of Object.keys(defaults)) {
      out[key] = coerce(key, parsed.filters[key], defaults, sanitize);
    }
    const storedSize = Number(parsed.pageSize);
    return { filters: out, pageSize: Number.isFinite(storedSize) && storedSize > 0 ? storedSize : null };
  } catch {
    // Corrupt or hand-edited JSON must never break the list.
    return null;
  }
}

/** Wipe every remembered list filter. Called on logout. */
export function clearAllListFilters() {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(LIST_FILTER_STORAGE_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* private mode / storage disabled — nothing to clear */
  }
}

export function useListFilters({
  storageKey,
  defaults,
  sanitize = {},
  defaultPageSize = 25,
  searchKey = 'search',
  searchDebounceMs = 350,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fullStorageKey = `${LIST_FILTER_STORAGE_PREFIX}${storageKey}`;

  // Callers pass object literals, so these would change identity every render.
  // Hold them in refs and keep them out of effect dependency lists.
  const defaultsRef = useRef(defaults);
  const sanitizeRef = useRef(sanitize);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Mount-only bootstrap.
  const initial = useMemo(() => {
    const d = defaultsRef.current;
    const s = sanitizeRef.current;
    const fromUrl = readFromUrl(searchParamsRef.current, d, s);
    // `page`/`page_size` count as URL signals too, so refreshing on page 4 of an
    // unfiltered list keeps you on page 4 instead of snapping back to page 1.
    const rawPage = searchParamsRef.current.get('page');
    const rawSize = searchParamsRef.current.get('page_size');
    if (fromUrl || rawPage !== null || rawSize !== null) {
      const urlPage = Number(rawPage);
      const urlSize = Number(rawSize);
      return {
        // A URL is a complete spec, not an overlay: filters absent from it fall
        // back to defaults rather than inheriting the remembered set.
        filters: fromUrl || { ...d },
        page: Number.isFinite(urlPage) && urlPage > 0 ? urlPage : 1,
        pageSize: Number.isFinite(urlSize) && urlSize > 0 ? urlSize : defaultPageSize,
      };
    }
    const stored = readFromStorage(fullStorageKey, d, s);
    if (stored) {
      // Page is intentionally not restored from storage — a stale page number
      // on a fresh visit is more confusing than useful. Refresh keeps it via URL.
      return { filters: stored.filters, page: 1, pageSize: stored.pageSize || defaultPageSize };
    }
    return { filters: { ...d }, page: 1, pageSize: defaultPageSize };
    // Empty deps is deliberate: this is a one-shot bootstrap. Later URL edits
    // are driven by this hook itself, not read back in.
  }, []);

  const [filters, setFiltersState] = useState(initial.filters);
  const [page, setPageState] = useState(initial.page);
  const [pageSize, setPageSizeState] = useState(initial.pageSize);
  const [debouncedSearch, setDebouncedSearch] = useState(initial.filters[searchKey] ?? '');

  // Keep typing responsive, but only commit the search term (to the URL, to
  // storage and to the caller's fetch) once it settles.
  const liveSearch = filters[searchKey] ?? '';
  useEffect(() => {
    if (liveSearch === debouncedSearch) return undefined;
    const t = setTimeout(() => setDebouncedSearch(liveSearch), searchDebounceMs);
    return () => clearTimeout(t);
  }, [liveSearch, debouncedSearch, searchDebounceMs]);

  /**
   * What the caller should fetch with: live filters, debounced search.
   *
   * Memoised on a content signature rather than on `filters` itself. `filters`
   * gets a new identity on every keystroke, and callers key their fetch
   * useCallback on this object — without the signature the debounce would be
   * defeated by identity churn and every keystroke would still fire a request.
   */
  const querySignature = JSON.stringify({ ...filters, [searchKey]: debouncedSearch });
  const queryFilters = useMemo(() => JSON.parse(querySignature), [querySignature]);

  // Mirror committed state into the URL.
  useEffect(() => {
    const d = defaultsRef.current;
    const current = searchParamsRef.current;
    const sp = new URLSearchParams(current);
    for (const key of Object.keys(d)) {
      const v = queryFilters[key];
      if (v === undefined || v === null || v === '' || v === d[key]) sp.delete(toParam(key));
      else sp.set(toParam(key), String(v));
    }
    if (page > 1) sp.set('page', String(page));
    else sp.delete('page');
    if (pageSize !== defaultPageSize) sp.set('page_size', String(pageSize));
    else sp.delete('page_size');

    if (sp.toString() === current.toString()) return;
    setSearchParams(sp, { replace: true });
  }, [queryFilters, page, pageSize, defaultPageSize, setSearchParams]);

  // Mirror committed state into localStorage (search deliberately excluded).
  useEffect(() => {
    const d = defaultsRef.current;
    const persisted = {};
    let allDefault = pageSize === defaultPageSize;
    for (const key of Object.keys(d)) {
      if (key === searchKey) continue;
      persisted[key] = queryFilters[key];
      if (queryFilters[key] !== d[key]) allDefault = false;
    }
    try {
      // Nothing worth remembering: drop the key rather than storing a no-op
      // entry, so Reset All genuinely clears it.
      if (allDefault) window.localStorage.removeItem(fullStorageKey);
      else {
        window.localStorage.setItem(
          fullStorageKey,
          JSON.stringify({ v: STORAGE_VERSION, filters: persisted, pageSize }),
        );
      }
    } catch {
      /* quota exceeded / private mode — persistence is best-effort */
    }
  }, [queryFilters, pageSize, defaultPageSize, fullStorageKey, searchKey]);

  const setFilter = useCallback((key, value) => {
    setFiltersState((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    // Any filter change returns to page 1. Doing this here rather than in an
    // effect on `filters` is what lets a restored page survive mount.
    setPageState(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState({ ...defaultsRef.current });
    setDebouncedSearch(defaultsRef.current[searchKey] ?? '');
    setPageState(1);
    try {
      window.localStorage.removeItem(fullStorageKey);
    } catch {
      /* nothing to clear */
    }
  }, [fullStorageKey, searchKey]);

  const setPageSize = useCallback((size) => {
    setPageSizeState(size);
    setPageState(1);
  }, []);

  /**
   * Drop a restored value that is no longer selectable — e.g. a department
   * saved under a different site. Returns `prev` untouched when already valid
   * so this can be called from an effect without looping.
   */
  const clearIfInvalid = useCallback((key, isValid) => {
    setFiltersState((prev) => {
      const fallback = defaultsRef.current[key];
      if (prev[key] === fallback || isValid(prev[key])) return prev;
      return { ...prev, [key]: fallback };
    });
  }, []);

  return {
    filters,
    queryFilters,
    setFilter,
    resetFilters,
    clearIfInvalid,
    page,
    setPage: setPageState,
    pageSize,
    setPageSize,
  };
}

export default useListFilters;
